import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Search, Heart, Loader2, Sparkles, Zap, MapPin, Plus, Check, Share2, Copy, MessageCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ini = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(' ');
  return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const filterTabs = ['All', 'Actors', 'Models', 'Performers', 'Available Now', 'Male', 'Female'];

const TalentDirectory = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSearching, setAiSearching] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Quick Book Modal State
  const [showQuickBook, setShowQuickBook] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [projects, setProjects] = useState([]);
  const [quickBooking, setQuickBooking] = useState(false);
  const [quickBookSuccess, setQuickBookSuccess] = useState(false);
  const [quickBookData, setQuickBookData] = useState({
    projectId: '', holdType: 'first', startDate: '', endDate: '', notes: ''
  });
  
  const [filters, setFilters] = useState({
    ageMin: 18, ageMax: 60, gender: '', location: '', experienceLevel: '', lookTags: [], availability: ''
  });
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => { fetchTalents(); fetchLookTags(); fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/projects`, { headers, withCredentials: true });
      setProjects(response.data);
    } catch (error) { console.error('Error fetching projects:', error); }
  };

  const fetchLookTags = async () => {
    try {
      const response = await axios.get(`${API}/look-tags`);
      setAvailableTags(response.data.tags);
    } catch (error) { console.error('Error fetching tags:', error); }
  };

  const fetchTalents = async (customFilters = null) => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      const f = customFilters || filters;
      if (f.ageMin > 18) params.append('age_min', f.ageMin);
      if (f.ageMax < 60) params.append('age_max', f.ageMax);
      if (f.gender) params.append('gender', f.gender);
      if (f.location) params.append('location', f.location);
      if (f.experienceLevel) params.append('experience_level', f.experienceLevel);
      if (f.lookTags.length > 0) params.append('look_tags', f.lookTags.join(','));
      if (f.availability) params.append('availability', f.availability);
      if (searchQuery && !aiSearching) params.append('search', searchQuery);
      const response = await axios.get(`${API}/talents?${params.toString()}`, { headers, withCredentials: true });
      setTalents(response.data);
    } catch (error) { console.error('Error fetching talents:', error); } 
    finally { setLoading(false); }
  };

  const handleAISearch = async () => {
    if (!searchQuery.trim()) return;
    setAiSearching(true); setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/ai-search`, { query: searchQuery }, { headers, withCredentials: true, timeout: 20000 });
      if (response.data.results?.length > 0) setTalents(response.data.results);
      else await fetchTalents();
    } catch (error) { await fetchTalents(); }
    finally { setAiSearching(false); setLoading(false); }
  };

  const handleFilterTab = (tab) => {
    setActiveFilter(tab);
    let newFilters = { ...filters, gender: '', availability: '' };
    if (tab === 'Male') newFilters.gender = 'male';
    else if (tab === 'Female') newFilters.gender = 'female';
    else if (tab === 'Available Now') newFilters.availability = 'available';
    // Actors/Models/Performers would need talent_type filter — use search for now
    setFilters(newFilters);
    fetchTalents(newFilters);
  };

  const clearFilters = () => {
    const defaultFilters = { ageMin: 18, ageMax: 60, gender: '', location: '', experienceLevel: '', lookTags: [], availability: '' };
    setFilters(defaultFilters);
    setSearchQuery('');
    setActiveFilter('All');
    fetchTalents(defaultFilters);
  };

  const openQuickBook = (talent, e) => {
    e.stopPropagation();
    setSelectedTalent(talent);
    setQuickBookData({
      projectId: projects[0]?.project_id || '', holdType: 'first',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], notes: ''
    });
    setQuickBookSuccess(false);
    setShowQuickBook(true);
  };

  const handleQuickBook = async () => {
    if (!selectedTalent || !quickBookData.projectId) return;
    setQuickBooking(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/shortlists`, { talent_id: selectedTalent.talent_id, project_id: quickBookData.projectId, notes: quickBookData.notes }, { headers, withCredentials: true }).catch(() => {});
      await axios.post(`${API}/holds`, { talent_id: selectedTalent.talent_id, project_id: quickBookData.projectId, hold_type: quickBookData.holdType, start_date: quickBookData.startDate, end_date: quickBookData.endDate, notes: quickBookData.notes }, { headers, withCredentials: true });
      setQuickBookSuccess(true);
      setTalents(prev => prev.map(t => t.talent_id === selectedTalent.talent_id ? { ...t, availability: 'hold' } : t));
      setTimeout(() => { setShowQuickBook(false); setQuickBookSuccess(false); }, 2000);
    } catch (error) { console.error('Quick book error:', error); }
    finally { setQuickBooking(false); }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.includes('drive.google.com')) {
      let fileId = null;
      if (url.includes('/file/d/')) fileId = url.split('/file/d/')[1]?.split('/')[0];
      else if (url.includes('id=')) fileId = url.split('id=')[1]?.split('&')[0];
      else if (url.includes('/d/')) fileId = url.split('/d/')[1]?.split('/')[0];
      if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
    }
    return url;
  };

  const defaultImages = [
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&q=80',
  ];

  return (
    <div style={{ background: 'var(--ink)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {/* NAV */}
      <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="directory-nav">
        <div className="flex items-center justify-between px-6 h-14 max-w-[1400px] mx-auto">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-7 h-7 bg-[var(--gold)] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)' }}>
              <span style={{ fontFamily: 'var(--display)', fontSize: 14, color: '#000', letterSpacing: 2 }}>C</span>
            </div>
            <span className="font-display text-xl" style={{ color: 'var(--cream)' }}>CASTDECK</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="nav-tab">Home</Link>
            <Link to="/talents" className="nav-tab active">Talent</Link>
            <Link to="/dashboard" className="nav-tab">Dashboard</Link>
            <Link to="/join-as-talent" className="nav-tab">Join as Talent</Link>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Button onClick={() => navigate('/create-profile')} className="btn-gold px-4 py-2 text-xs flex items-center gap-1" data-testid="add-talent-btn">
              <Plus className="w-3 h-3" /> Add Talent
            </Button>
          </div>
        </div>
      </nav>

      {/* Directory Hero */}
      <div className="dir-hero-v3" style={{ marginTop: 56 }} data-testid="dir-hero">
        <div className="dir-bg" />
        <div className="dir-overlay" />
        <div className="relative z-10 flex items-end justify-between w-full px-6 pb-6 max-w-[1400px] mx-auto">
          <div className="font-display text-4xl md:text-5xl" style={{ lineHeight: 1, letterSpacing: 3, color: 'var(--cream)' }}>
            TALENT<br/><span className="gradient-gold">DIRECTORY</span>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl gradient-gold" data-testid="dir-count">{talents.length}</div>
            <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Profiles shown</div>
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="px-6 py-5 max-w-[1400px] mx-auto" data-testid="dir-controls">
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="6" cy="6" r="5" stroke="var(--muted-c)" strokeWidth="1.5"/>
              <path d="M10 10l4 4" stroke="var(--muted-c)" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAISearch()}
              placeholder="Name, type, city, skills…"
              className="w-full h-10 pl-9 pr-4 text-sm"
              style={{ background: 'var(--surface)', border: '1px solid rgba(255,255,255,.08)', color: 'var(--cream)', fontFamily: 'var(--body)', outline: 'none' }}
              data-testid="search-input"
            />
          </div>
          <button onClick={handleAISearch} className="btn-gold h-10 px-5 flex items-center gap-2 text-xs" disabled={aiSearching} data-testid="ai-search-btn">
            {aiSearching ? <Loader2 className="w-4 h-4 spinner" /> : <Sparkles className="w-4 h-4" />}
            AI Search
          </button>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2">
          {filterTabs.map(tab => (
            <button
              key={tab}
              onClick={() => handleFilterTab(tab)}
              className={`fp ${activeFilter === tab ? 'active' : ''}`}
              data-testid={`filter-${tab.toLowerCase().replace(' ', '-')}`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Talent Grid */}
      <div className="px-6 pb-12 max-w-[1400px] mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-2 border-[var(--gold)] border-t-transparent rounded-full spinner" />
          </div>
        ) : talents.length > 0 ? (
          <div className="talent-grid-v3" data-testid="talent-grid">
            {talents.map((talent, index) => (
              <TalentCardV3
                key={talent.talent_id}
                talent={talent}
                defaultImage={defaultImages[index % defaultImages.length]}
                onQuickBook={openQuickBook}
                getImageUrl={getImageUrl}
              />
            ))}
          </div>
        ) : (
          <div className="empty-state py-24">
            <div className="text-4xl mb-4 opacity-20">🎭</div>
            <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--cream)' }}>No talents found</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--warm)' }}>Try adjusting your filters or search query</p>
            <button onClick={clearFilters} className="btn-gold px-6 py-2 text-xs" data-testid="clear-filters-empty">Clear Filters</button>
          </div>
        )}
      </div>

      {/* Quick Book Modal */}
      <Dialog open={showQuickBook} onOpenChange={setShowQuickBook}>
        <DialogContent className="border-border sm:max-w-md" style={{ background: 'var(--ink2)', borderColor: 'rgba(255,255,255,.08)' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" style={{ color: 'var(--gold)' }} />
              <span style={{ color: 'var(--cream)' }}>Quick Book</span>
            </DialogTitle>
            <DialogDescription style={{ color: 'var(--warm)' }}>
              Place a hold on {selectedTalent?.full_name} in one click
            </DialogDescription>
          </DialogHeader>
          
          {quickBookSuccess ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(46,204,138,.1)' }}>
                <Check className="w-8 h-8" style={{ color: 'var(--green)' }} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--cream)' }}>Hold Placed!</h3>
              <p style={{ color: 'var(--warm)' }}>{selectedTalent?.full_name} is now on hold</p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedTalent && (
                <div className="flex items-center gap-4 p-3" style={{ background: 'var(--faint)', border: '1px solid rgba(255,255,255,.06)' }}>
                  <img src={getImageUrl(selectedTalent.photos?.[0]) || defaultImages[0]} alt={selectedTalent.full_name} className="w-14 h-14 object-cover" />
                  <div>
                    <h4 className="font-bold text-sm" style={{ color: 'var(--cream)' }}>{selectedTalent.full_name}</h4>
                    <p className="text-xs" style={{ color: 'var(--warm)' }}>{selectedTalent.age} yrs · {selectedTalent.location}</p>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: 'var(--muted-c)' }}>Select Project</Label>
                {projects.length > 0 ? (
                  <Select value={quickBookData.projectId} onValueChange={(v) => setQuickBookData(prev => ({ ...prev, projectId: v }))}>
                    <SelectTrigger style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)' }} data-testid="quick-book-project"><SelectValue placeholder="Choose a project" /></SelectTrigger>
                    <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <div className="p-3 text-xs" style={{ background: 'var(--faint)', color: 'var(--warm)' }}>No projects yet. <Link to="/projects" style={{ color: 'var(--gold)' }}>Create one first</Link></div>
                )}
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: 'var(--muted-c)' }}>Hold Type</Label>
                <Select value={quickBookData.holdType} onValueChange={(v) => setQuickBookData(prev => ({ ...prev, holdType: v }))}>
                  <SelectTrigger style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)' }} data-testid="quick-book-hold-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first">First Hold (Priority)</SelectItem>
                    <SelectItem value="second">Second Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: 'var(--muted-c)' }}>Start Date</Label>
                  <Input type="date" value={quickBookData.startDate} onChange={(e) => setQuickBookData(prev => ({ ...prev, startDate: e.target.value }))} style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)' }} data-testid="quick-book-start" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: 'var(--muted-c)' }}>End Date</Label>
                  <Input type="date" value={quickBookData.endDate} onChange={(e) => setQuickBookData(prev => ({ ...prev, endDate: e.target.value }))} style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)' }} data-testid="quick-book-end" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: 'var(--muted-c)' }}>Notes (Optional)</Label>
                <Textarea value={quickBookData.notes} onChange={(e) => setQuickBookData(prev => ({ ...prev, notes: e.target.value }))} placeholder="Any special requirements..." style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', height: 60 }} data-testid="quick-book-notes" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowQuickBook(false)} className="btn-ghost px-5 py-2 text-xs">Cancel</button>
                <button onClick={handleQuickBook} className="btn-gold px-5 py-2 text-xs flex items-center gap-2" disabled={quickBooking || !quickBookData.projectId} data-testid="confirm-quick-book">
                  {quickBooking ? <Loader2 className="w-4 h-4 spinner" /> : <Zap className="w-4 h-4" />}
                  Place Hold
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ═══ TALENT CARD V3 ═══ */
const TalentCardV3 = ({ talent, defaultImage, onQuickBook, getImageUrl }) => {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const image = getImageUrl(talent.photos?.[0]) || defaultImage;

  const handleShare = async (method, e) => {
    e.stopPropagation();
    const talentUrl = `${window.location.origin}/talents/${talent.talent_id}`;
    const shareText = `Check out ${talent.full_name} on CastDeck - ${talent.age} yrs, ${talent.location}`;
    if (method === 'copy') { try { await navigator.clipboard.writeText(talentUrl); } catch {} }
    else if (method === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n' + talentUrl)}`, '_blank');
    else if (method === 'email') window.open(`mailto:?subject=${encodeURIComponent('CastDeck - ' + talent.full_name)}&body=${encodeURIComponent(shareText + '\n\n' + talentUrl)}`, '_blank');
  };

  return (
    <div className="tc-v3" onClick={() => navigate(`/talents/${talent.talent_id}`)} data-testid={`talent-card-${talent.talent_id}`}>
      <div className="tc-img-wrap">
        <div className="tc-init-bg">{ini(talent.full_name)}</div>
        <img src={image} alt={talent.full_name} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />

        {/* Availability badge */}
        <div className="absolute top-2 left-2 z-10 px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase"
             style={{ 
               background: talent.availability === 'available' ? 'var(--green)' : talent.availability === 'hold' ? 'var(--gold)' : 'var(--red)', 
               color: '#fff' 
             }}>
          {talent.availability === 'available' ? 'Available' : talent.availability === 'hold' ? 'On Hold' : 'Busy'}
        </div>

        {/* Heart button */}
        <button
          onClick={(e) => { e.stopPropagation(); setLiked(!liked); }}
          className="absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center transition-colors"
          style={{ background: 'rgba(0,0,0,.4)', color: liked ? 'var(--red)' : 'var(--warm)' }}
          data-testid={`shortlist-${talent.talent_id}`}
        >
          {liked ? '♥' : '♡'}
        </button>
      </div>

      {/* Card body */}
      <div className="p-3">
        <div className="text-sm font-bold mb-0.5" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>{talent.full_name}</div>
        <div className="text-[11px] mb-2" style={{ color: 'var(--warm)' }}>
          {talent.talent_type || 'Actor'} · {talent.age} · <span className="capitalize">{talent.gender}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--muted-c)' }}>
            <MapPin className="w-3 h-3" /> {talent.location}
          </span>
          <span className="text-[10px] font-bold cursor-pointer" style={{ color: 'var(--gold)', fontFamily: 'var(--head)' }}
                onClick={(e) => onQuickBook && talent.availability === 'available' ? onQuickBook(talent, e) : e.stopPropagation()}>
            + Shortlist
          </span>
        </div>
      </div>
    </div>
  );
};

export default TalentDirectory;
